import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Divider
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  PlayArrow as PlayIcon,
  VolumeUp as VolumeIcon
} from '@mui/icons-material';
import { apiService } from '../../services/api';

interface TranscriptionReviewModalProps {
  open: boolean;
  onClose: () => void;
  jobId: string;
  onProceed: () => void;
}

const TranscriptionReviewModal: React.FC<TranscriptionReviewModalProps> = ({
  open,
  onClose,
  jobId,
  onProceed
}) => {
  const [transcribedText, setTranscribedText] = useState('');
  const [originalText, setOriginalText] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (open && jobId) {
      loadTranscription();
    }
  }, [open, jobId]);

  const loadTranscription = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await apiService.getJobTranscription(jobId);
      setTranscribedText(data.transcribedText);
      setOriginalText(data.originalText);
      setHasChanges(false);
    } catch (error) {
      setError('Failed to load transcription');
      console.error('Error loading transcription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      await apiService.updateJobTranscription(jobId, transcribedText);
      setIsEditing(false);
      setHasChanges(false);
    } catch (error) {
      setError('Failed to save transcription');
      console.error('Error saving transcription:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setTranscribedText(originalText);
    setIsEditing(false);
    setHasChanges(false);
  };

  const handleTextChange = (value: string) => {
    setTranscribedText(value);
    setHasChanges(value !== originalText);
  };

  const handleProceed = () => {
    if (hasChanges) {
      handleSave().then(() => {
        onProceed();
      });
    } else {
      onProceed();
    }
  };

  const wordCount = transcribedText.split(/\s+/).filter(word => word.length > 0).length;
  const characterCount = transcribedText.length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VolumeIcon color="primary" />
          <Typography variant="h6">Review Transcribed Text</Typography>
          <Chip 
            label="Audio Processing" 
            color="info" 
            size="small" 
            variant="outlined"
          />
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <Box>
            {/* Statistics */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Transcription Statistics
              </Typography>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Chip 
                  label={`${wordCount} words`} 
                  size="small" 
                  variant="outlined"
                />
                <Chip 
                  label={`${characterCount} characters`} 
                  size="small" 
                  variant="outlined"
                />
                {hasChanges && (
                  <Chip 
                    label="Modified" 
                    color="warning" 
                    size="small"
                  />
                )}
              </Box>
            </Box>

            {/* Transcription Text */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Transcribed Text
                </Typography>
                {!isEditing && (
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => setIsEditing(true)}
                    variant="outlined"
                  >
                    Edit
                  </Button>
                )}
              </Box>
              
              <TextField
                multiline
                rows={12}
                fullWidth
                value={transcribedText}
                onChange={(e) => handleTextChange(e.target.value)}
                disabled={!isEditing}
                variant="outlined"
                placeholder="Transcribed text will appear here..."
                sx={{
                  '& .MuiInputBase-root': {
                    fontFamily: 'monospace',
                    fontSize: '0.9rem'
                  }
                }}
              />
            </Box>

            {/* Instructions */}
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Instructions:</strong> Review the transcribed text above. You can edit it to correct any 
                speech-to-text errors, improve clarity, or add missing information. Once you're satisfied, 
                click "Proceed to Story Generation" to continue.
              </Typography>
            </Alert>

            {/* Edit Actions */}
            {isEditing && (
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                  size="small"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                  disabled={saving}
                  size="small"
                >
                  Cancel
                </Button>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} disabled={loading || saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleProceed}
          disabled={loading || saving || !transcribedText.trim()}
          startIcon={<PlayIcon />}
        >
          {hasChanges ? 'Save & Proceed to Story Generation' : 'Proceed to Story Generation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TranscriptionReviewModal;
